import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import type { ScamImageData } from "../types";
import { PickScamResult } from "../types";
import { IMAGE_DATABASE } from "../variables";

const Page = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1024px;
  margin: 0 auto;
  padding: 2rem 1rem;
  box-sizing: border-box;
`;

const Title = styled.h1`
  margin: 0.5rem 0;
  font-family: "PingFang TC";
  font-style: normal;
  font-weight: 600;
  font-size: 28px;
  text-align: center;
  letter-spacing: 0.02em;

  background: linear-gradient(
    90deg,
    #757ea7 -8.1%,
    #ffffff 53.63%,
    #3e466d 108.63%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const SubTitle = styled.p`
  margin: 0 0 2rem 0;
  font-family: "PingFang TC";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: 1px;
`;

const ImagesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  width: 100%;
  max-width: 800px;
  margin-bottom: 2rem;

  @media (max-width: 767px) {
    gap: 1rem;
  }
`;

const ImageCard = styled.div<{ $selected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:active {
    transform: scale(0.98);
  }
`;

const ImageWrapper = styled.div<{ $selected: boolean }>`
  position: relative;
  width: 100%;
  aspect-ratio: 9 / 16;
  border-radius: 12px;
  overflow: hidden;
  border: 3px solid ${(props) => (props.$selected ? "#1E90FF" : "transparent")};
  box-shadow: ${(props) =>
    props.$selected
      ? "0px 0px 20px rgba(30, 144, 255, 0.5)"
      : "0px 4px 8px rgba(0, 0, 0, 0.2)"};
  transition: all 0.3s ease;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  user-select: none;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  font-family: "PingFang TC";
  font-size: 16px;
  font-weight: 500;
  color: #ffffff;
  user-select: none;

  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const RoundCheckbox = styled.input.attrs({ type: "checkbox" })`
  appearance: none;
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  background-color: transparent;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;

  &:checked {
    background-color: #1e90ff;
    border-color: #1e90ff;
  }

  &:checked::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #ffffff;
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.7);
  }
`;

const SubmitButton = styled.button`
  margin-top: 8px;
  padding: 10px 40px;
  box-sizing: border-box;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 1px;
  line-height: 1;

  border: none;
  background: linear-gradient(360deg, #0c34f8 0%, #90b3ff 100%);
  box-shadow: 0px 0px 32.8px #134eff, 0px 4px 6.1px rgba(0, 0, 0, 0.25);
  border-radius: 110px;

  transition: filter 0.15s ease, transform 0.1s ease;
  &:hover {
    filter: brightness(1.07);
  }
  &:active {
    transform: scale(0.99);
  }
  cursor: pointer;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 32px;
    min-height: 80px;
    min-width: 240px;
  }
`;

export default function PickScamPage() {
  const navigate = useNavigate();
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [displayedImages, setDisplayedImages] = useState<ScamImageData[]>([]);

  // 随机选择两张图片
  useEffect(() => {
    const shuffled = [...IMAGE_DATABASE].sort(() => Math.random() - 0.5);
    setDisplayedImages(shuffled.slice(0, 2));
  }, []);

  const handleImageClick = (imageId: string) => {
    setSelectedImages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  };

  const handleSubmit = () => {
    const result: PickScamResult = {
      questions: displayedImages.map((img) => img.id),
      answers: Array.from(selectedImages),
      timestamp: Date.now(),
    };

    navigate("/verifying", { state: result });
  };

  return (
    <Page>
      <Title>請選擇您認為的詐騙訊息</Title>
      <SubTitle>(可複選)</SubTitle>

      <ImagesContainer>
        {displayedImages.map((imageData) => {
          const isSelected = selectedImages.has(imageData.id);
          return (
            <ImageCard
              key={imageData.id}
              $selected={isSelected}
              onClick={() => handleImageClick(imageData.id)}
            >
              <CheckboxLabel>
                <RoundCheckbox
                  checked={isSelected}
                  onChange={() => handleImageClick(imageData.id)}
                  onClick={(e) => e.stopPropagation()}
                />
                這是詐騙
              </CheckboxLabel>

              <ImageWrapper $selected={isSelected}>
                <Image src={imageData.path} alt="Chief message" />
              </ImageWrapper>
            </ImageCard>
          );
        })}
      </ImagesContainer>

      <SubmitButton onClick={handleSubmit}>查看結果</SubmitButton>
    </Page>
  );
}
